import {
  reportingPrisma as prisma,
  BookableItemType,
  type Prisma,
} from "client";

const notDeleted: Prisma.BookableItemWhereInput = {
  OR: [{ deletedAt: null }, { deletedAt: { isSet: false } }],
};

const paidBookableItemWhere: Prisma.BookableItemWhereInput = {
  ...notDeleted,
  AND: [
    {
      OR: [{ price: { gt: 0 } }, { prices: { some: { price: { gt: 0 } } } }],
    },
    {
      OR: [
        {
          type: BookableItemType.TICKET,
          ticket: {
            is: {
              OR: [{ deletedAt: null }, { deletedAt: { isSet: false } }],
            },
          },
        },
        {
          type: BookableItemType.ENTITY_TIER,
          entityTier: {
            is: {
              OR: [{ deletedAt: null }, { deletedAt: { isSet: false } }],
            },
          },
        },
        {
          type: BookableItemType.ENTITY,
          entities: {
            some: {
              OR: [{ deletedAt: null }, { deletedAt: { isSet: false } }],
            },
          },
        },
      ],
    },
  ],
};

/**
 * Filters on the local `eventId` FK (BookableItem(eventId, …) indexes) rather than
 * `event: { … }`, which Prisma turns into a $lookup on Event followed by a COLLSCAN.
 */
const hasPaidBookableItem = async (
  eventIdFilter: NonNullable<Prisma.BookableItemWhereInput["eventId"]>
): Promise<boolean> => {
  try {
    const paidItem = await prisma.bookableItem.findFirst({
      where: {
        ...paidBookableItemWhere,
        eventId: eventIdFilter,
      },
      select: { id: true },
    });

    return Boolean(paidItem);
  } catch (error) {
    return false;
  }
};

const notDeletedEvent: Prisma.EventWhereInput = {
  OR: [{ deletedAt: null }, { deletedAt: { isSet: false } }],
};

const hasPaidBookableItemForEvents = async (
  eventWhere: Prisma.EventWhereInput
): Promise<boolean> => {
  try {
    const events = await prisma.event.findMany({
      where: eventWhere,
      select: { id: true },
    });
    if (events.length === 0) return false;
    return hasPaidBookableItem({ in: events.map((event) => event.id) });
  } catch (error) {
    return false;
  }
};

/**
 * Event is "paid" when a non-deleted BookableItem (TICKET, ENTITY_TIER, ENTITY)
 * exists with price > 0 or a related BookableItemPrice > 0.
 * Do not use order.total or payment records for this check.
 */
export const isPayedEvent = async (eventId: string): Promise<boolean> =>
  hasPaidBookableItem(eventId);

/** True when this white label includes at least one paid event. */
export const hasPaidEventsForWhiteLabel = async (
  whiteLabelId: string
): Promise<boolean> =>
  hasPaidBookableItemForEvents({ whiteLabelId, ...notDeletedEvent });

/** True when this organization includes at least one paid event. */
export const hasPaidEventsForOrganization = async (
  organizationId: string
): Promise<boolean> =>
  hasPaidBookableItemForEvents({
    organizationIds: { has: organizationId },
    ...notDeletedEvent,
  });
